import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Users, Vote, Flame, Timer, Crown, Zap } from "lucide-react";
import { fmtCountdown } from "../lib/api";

const StatTile = ({ icon: Icon, label, value, accent, testid }) => (
  <div
    data-testid={testid}
    className="rounded-2xl border border-white/10 bg-[#0c0f17]/80 backdrop-blur-xl p-5"
  >
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-400 font-semibold">
      <Icon size={14} style={{ color: accent }} /> {label}
    </div>
    <div className="font-mono-num font-black text-2xl sm:text-3xl mt-2 truncate">{value}</div>
  </div>
);

const mask = (id) => (id ? `${id.slice(0, 4)}…${id.slice(-3)}` : "anon");

export const CommunityActivity = ({ community, recent, shuffleSecs }) => {
  return (
    <section id="community" className="px-4 sm:px-6 lg:px-8 py-12 scroll-mt-20">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-7">
          <span className="relative flex h-3 w-3">
            <span className="ping-ring absolute inline-flex h-full w-full rounded-full bg-[#ff007a]" />
            <span className="relative inline-flex h-3 w-3 rounded-full bg-[#ff007a]" />
          </span>
          <h2 className="font-display font-extrabold uppercase tracking-tight text-2xl sm:text-3xl">
            Community Activity
          </h2>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatTile
            testid="stat-total-votes"
            icon={Vote}
            label="Total Votes"
            value={community.total_votes.toLocaleString()}
            accent="#00ff66"
          />
          <StatTile
            testid="stat-active-voters"
            icon={Users}
            label="Active Voters"
            value={community.active_voters.toLocaleString()}
            accent="#00f0ff"
          />
          <StatTile
            testid="stat-leader"
            icon={Crown}
            label="Current Leader"
            value={community.leader_name || "—"}
            accent="#ffd028"
          />
          <StatTile
            testid="stat-time-remaining"
            icon={Timer}
            label="Next Shuffle"
            value={fmtCountdown(shuffleSecs)}
            accent="#ff007a"
          />
        </div>

        <div className="grid lg:grid-cols-3 gap-4 mt-4">
          <div className="rounded-2xl border border-white/10 bg-[#0c0f17]/80 backdrop-blur-xl p-5">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-400 font-semibold">
              <Flame size={14} className="text-[#ff5500]" /> Most Voted This Round
            </div>
            {community.most_voted ? (
              <div className="mt-3 flex items-center gap-3">
                <img
                  src={community.most_voted.image}
                  alt=""
                  className="h-10 w-10 rounded-full border border-white/20 object-cover"
                />
                <div>
                  <div className="font-display font-bold">{community.most_voted.name}</div>
                  <div className="font-mono-num text-sm text-[#ff5500]">
                    ${community.most_voted.symbol} · {community.most_voted.votes} votes
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-3 text-sm text-slate-500">
                No votes yet — cast the first one to crown a leader.
              </div>
            )}
          </div>

          {/* recent votes stream */}
          <div
            data-testid="recent-votes-stream"
            className="lg:col-span-2 rounded-2xl border border-white/10 bg-[#0c0f17]/80 backdrop-blur-xl p-5"
          >
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-3">
              <Zap size={14} className="text-[#00ff66]" /> Live Vote Stream
            </div>
            {recent.length === 0 ? (
              <div className="text-sm text-slate-500">Waiting for the community…</div>
            ) : (
              <div className="space-y-1.5 max-h-52 overflow-y-auto pr-1">
                <AnimatePresence initial={false}>
                  {recent.map((v) => (
                    <motion.div
                      key={`${v.device_id}-${v.ts}`}
                      initial={{ opacity: 0, x: -12 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="flex items-center justify-between text-sm py-1.5 border-b border-white/5 last:border-0"
                    >
                      <span className="text-slate-300 font-mono-num">
                        <span className="text-slate-500">{mask(v.device_id)}</span> sent it for{" "}
                        <span className="text-[#00ff66] font-bold">${v.coin_symbol}</span>
                      </span>
                      <span className="text-xs text-slate-600 font-mono-num shrink-0">
                        {timeAgo(v.ts)}
                      </span>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

function timeAgo(iso) {
  const diff = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}
